import { Box, ButtonBase, styled, type SxProps, type Theme } from '@mui/material';
import { isBefore } from 'date-fns';
import React, { useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { UnderlinedText } from '@/components/UnderlinedText/index.ts';
import { useIsAfterDate } from '@/hooks/useIsAfterDate.ts';
import type { Competition, CompetitionWinner } from '@/lib/api/requests.ts';
import { createDateInNorwegianTime, type NoDate } from '@/services/new-utils.ts';
import { Button } from '@/ui/Button.tsx';
import { Timer, type TimerTheme } from '@/ui/Timer.tsx';

import CalendarIcon from './calendar-icon.svg?react';

const TileRoot = styled('div')(({ theme }) => ({
  position: 'relative',
  isolation: 'isolate',
  aspectRatio: '3 / 4',
  minHeight: '360px',
  maxWidth: '100%',
  borderRadius: '16px',

  '&[data-big="true"]': {
    [theme.breakpoints.up('sm')]: {
      gridColumn: 'span 2',
      aspectRatio: '6.2 / 4', // A bit more than 2x to account for the gap
      height: '100%', // Extend to fit
    },
  },
}));

const Card = styled('div')(({ theme }) => ({
  borderRadius: 'inherit',
  overflow: 'hidden',
  height: '100%',
  // Helps with border radius glitches (for some reason)
  WebkitMaskImage: '-webkit-radial-gradient(white, black)',
  backgroundColor: theme.palette.background.contrast,
  color: 'white',
}));

const Content = styled('div')(({ theme }) => ({
  padding: '20px',
  paddingBottom: '24px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',

  '[data-big="true"] &': {
    [theme.breakpoints.up('sm')]: {
      paddingLeft: '30px',
      paddingRight: '30px',
    },
  },
}));

export type CompetitionTileConfig = {
  color?: string;
  backgroundColor?: string;
  backgroundSrc?: string;
  backgroundBigSrc?: string;
  winnersBgColor?: string;
  endDate: NoDate;
  href: string;
  timerTheme?: Partial<TimerTheme>;
};

export type CompetitionTileProps = {
  competition: Competition;
  config: CompetitionTileConfig;
  big?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
};

const dates = Array.from({ length: 24 }, (_, i) => createDateInNorwegianTime(2025, 12, i + 1, 12));

const getAdventTimerDate = () => {
  const now = new Date();
  return dates.find((date) => isBefore(now, date)) || dates.at(-1)!;
};

export const CompetitionTile: React.FC<CompetitionTileProps> = ({
  competition,
  config,
  big,
  sx,
  className,
}) => {
  const endDate =
    competition.slug === 'advent_2025_campaign' ? getAdventTimerDate() : config.endDate;
  const ended = useIsAfterDate(endDate);
  const isAfterFirstAdventDraw = useIsAfterDate(dates[0]);

  // In some cases big bg cannot be shown at all
  const canShowBigBackground = !!(big && config.backgroundBigSrc);

  return (
    <TileRoot sx={sx} className={className} data-big={big}>
      <Card
        sx={{
          color: config?.color,
          backgroundColor: config?.backgroundColor,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* <picture> makes the browser download only the matching image, two <img>s hidden with CSS would both be fetched (and preloaded by React) */}
        <picture className="absolute inset-0 z-[-1]">
          {canShowBigBackground && (
            <source media="(min-width: 40rem)" srcSet={config.backgroundBigSrc} />
          )}
          <img className="h-full w-full object-cover" src={config.backgroundSrc} alt="" />
        </picture>
        <Content>
          {ended ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                mb: 2,
              }}
            >
              <CalendarIcon />
              <div className="typography-v1-body1 sm:typography-v1-body3">{competition.month}</div>
            </Box>
          ) : (
            <Box
              sx={{
                mb: 3,
              }}
            />
          )}

          <h2 className="mb-2 typography-v1-h1 whitespace-pre-line sm:typography-v1-h3">
            {formatSubtitle(competition.title)}
          </h2>

          {competition.subtitle && (
            <p className="mb-4 typography-v1-h3 whitespace-pre-line sm:typography-v1-h4">
              {formatSubtitle(competition.subtitle)}
            </p>
          )}

          {(ended ||
            (competition.slug === 'advent_2025_campaign' && competition.winners.length > 0)) && (
            <Winners winners={competition.winners} config={config} />
          )}

          {!ended && (
            <>
              <p className="mb-2 typography-v1-h5">
                {competition.slug === 'advent_2025_campaign'
                  ? isAfterFirstAdventDraw
                    ? 'Neste luke åpnes om:'
                    : 'Første luke åpnes om:'
                  : 'Trekkes om:'}
              </p>
              <Timer date={endDate} theme={config.timerTheme} />
            </>
          )}

          <Button
            color="yellow"
            size="small"
            sx={{ mt: 'auto', width: 'auto', height: '48px' }}
            // @ts-expect-error
            component={Link}
            to={config.href}
            prefetch="viewport"
          >
            Les mer
          </Button>
        </Content>
      </Card>
      {!ended && (
        <Box sx={{ position: 'absolute', top: '-20px' }}>
          <Bubble className="typography-v1-caption1">PÅGÅENDE TREKNING</Bubble>
        </Box>
      )}
    </TileRoot>
  );
};

export type WinnersProps = {
  winners: CompetitionWinner[];
  config: CompetitionTileConfig;
};

const Winners: React.FC<WinnersProps> = ({ winners, config }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box
      sx={{
        borderRadius: '4px',
        backgroundColor: config.winnersBgColor,
        padding: '8px 12px',
        whiteSpace: 'pre-line',
      }}
    >
      <div className="line-height-1.75 max-w-[20ch] typography-v1-body2 sm:typography-v1-caption1">
        {winners.length === 0 ? (
          <>ingen vinnere ennå</>
        ) : (
          <>
            {winners[0].name}
            <br />
            {winners[0].age} • {winners[0].city}
          </>
        )}

        {winners.length > 1 && (
          <>
            <br />
            <ButtonBase
              onClick={() => setIsOpen(true)}
              sx={{ fontWeight: '600', textDecoration: 'underline' }}
            >
              Vis alle {winners.length} vinnere
            </ButtonBase>
          </>
        )}
      </div>

      <CompetitionWinnersDialog open={isOpen} onClose={() => setIsOpen(false)} winners={winners} />
    </Box>
  );
};

const formatSubtitle = (text: string) => {
  let o = reactReplaceString(text, /<u>(.*)<\/u>/, (match, i) => (
    <UnderlinedText key={i}>{match}</UnderlinedText>
  ));

  o = reactReplaceString(o, /<b>(.*)<\/b>/, (match, i) => <b key={i}>{match}</b>);

  return o;
};

const reactReplaceString = (
  str: string | Array<ReactNode>,
  regex: RegExp,
  fn: (match: string, index: number) => ReactNode,
  startingIndex = 0,
) => {
  const id = (
    (n) => () =>
      n++
  )(startingIndex);

  const replace = (s: string) => s.split(regex).map((v, i) => (i % 2 === 0 ? v : fn(v, id())));

  if (!Array.isArray(str)) {
    str = [str];
  }
  return str.flatMap((x) => (typeof x === 'string' ? replace(x) : x));
};

const Bubble = styled('div')(({ theme }) => ({
  '--bg-color': theme.palette.background.contrast,
  '--pointer-size': '10px',

  position: 'relative',
  backgroundColor: 'var(--bg-color)',
  color: 'white',
  padding: theme.spacing(1, 2),
  borderRadius: '8px',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '10px',
    width: 0,
    height: 0,
    border: 'var(--pointer-size) solid transparent',
    borderTopColor: 'var(--bg-color)',
    borderBottom: 0,
    borderRight: 0,
    marginLeft: 'calc(var(--pointer-size) / 2 * -1 - 1px)',
    marginBottom: 'calc(var(--pointer-size) * -1 + 1px)',
  },
}));
